import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { ObservableLevelsEnum, PartName } from '../../../../data/enums';
import { CompleteButton } from '../../../../components/atoms/Button';
import { useEffect, useState } from 'react';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';

export const EngagementScreen = () => {
  const { externalId, sideName, partCategory } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const { equipmentPart, error: errorEquipmentPart } =
    useAssetEquipmentPartCached({
      externalId,
      asset: inspection?.asset,
      sideName,
      partCategory: partCategory,
      partName: PartName.tumblerEngagement,
    });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    async function getEquipment() {
      try {
        if (errorEquipmentPart) {
          setError(errorEquipmentPart);
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    getEquipment();
  }, [equipmentPart, errorEquipmentPart]);

  if (!equipmentPart || !externalId || !undercarriageCategory) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div className="-mt-4">
        <div className="sticky -top-4 z-10 bg-white pt-4">
          <ScreenHeading
            eyebrow={'Drive tumbler'}
            tag={sideName?.split('-')[0]}
            title={'Engagement'}
            partNumber={equipmentPart.part.partNumber}
            tooltipKey={'undercarriage-drive-tumbler-engagement-screen-header'}
          />
        </div>

        <ScreenObservationLayout className="mt-4">
          <PhotoSelector
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            title="Photos"
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage
                .TumblerEngagement.Photo
            }
          />
          <RichTextField
            label="Comments"
            className="mt-4"
            path={
              ObservableItemIdentifier.Identifiers.Undercarriage
                .TumblerEngagement.Comments
            }
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
